import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Plus } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_CATEGORIES = ['U11', 'U13', 'U15', 'U17', 'U19', 'Open'];

const AddMatchDialog = ({ onMatchAdded, autoOpen = false }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [matchType, setMatchType] = useState('singles');
  const [isPractice, setIsPractice] = useState(false);
  const [bestOfThree, setBestOfThree] = useState(false);
  const [formData, setFormData] = useState({
    opponent_name: '',
    teammate_name: '',
    opponent2_name: '',
    tournament_name: '',
    age_category: '',
    match_date: new Date().toISOString().split('T')[0],
    user_score: '',
    opponent_score: '',
    game1_user_score: '',
    game1_opponent_score: '',
    game2_user_score: '',
    game2_opponent_score: '',
    game3_user_score: '',
    game3_opponent_score: '',
    video_url: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  const resetForm = () => {
    setMatchType('singles');
    setIsPractice(false);
    setBestOfThree(false);
    setFormData({
      opponent_name: '',
      teammate_name: '',
      opponent2_name: '',
      tournament_name: '',
      age_category: '',
      match_date: new Date().toISOString().split('T')[0],
      user_score: '',
      opponent_score: '',
      game1_user_score: '',
      game1_opponent_score: '',
      game2_user_score: '',
      game2_opponent_score: '',
      game3_user_score: '',
      game3_opponent_score: '',
      video_url: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        match_type: matchType,
        is_practice: isPractice,
        age_category: formData.age_category || null,
        tournament_name: isPractice ? null : (formData.tournament_name || null),
        opponent_name: formData.opponent_name,
        teammate_name: matchType === 'doubles' ? formData.teammate_name : null,
        opponent2_name: matchType === 'doubles' ? formData.opponent2_name : null,
        best_of_three: bestOfThree,
        match_date: formData.match_date || null,
        video_url: formData.video_url || null,
        notes: formData.notes || null
      };

      if (bestOfThree) {
        payload.game1_user_score = parseInt(formData.game1_user_score);
        payload.game1_opponent_score = parseInt(formData.game1_opponent_score);
        payload.game2_user_score = parseInt(formData.game2_user_score);
        payload.game2_opponent_score = parseInt(formData.game2_opponent_score);
        payload.game3_user_score = formData.game3_user_score ? parseInt(formData.game3_user_score) : null;
        payload.game3_opponent_score = formData.game3_opponent_score ? parseInt(formData.game3_opponent_score) : null;
        payload.user_score = parseInt(formData.game1_user_score);
        payload.opponent_score = parseInt(formData.game1_opponent_score);
      } else {
        payload.user_score = parseInt(formData.user_score);
        payload.opponent_score = parseInt(formData.opponent_score);
      }

      await axios.post(
        `${API}/matches`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Match logged!',
        description: `Your ${matchType}${isPractice ? ' practice' : ''} match has been added.`
      });

      resetForm();
      setOpen(false);
      onMatchAdded();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to log match. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-blue-600 hover:bg-blue-700" data-testid="log-match-btn">
          <Plus className="mr-2 h-4 w-4" />
          Log New Match
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base">Log New Match</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-3">
            {/* Row: Type + Date */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Match Type</Label>
                <Select value={matchType} onValueChange={setMatchType}>
                  <SelectTrigger data-testid="match-type-select" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="singles">Singles</SelectItem>
                    <SelectItem value="doubles">Doubles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Match Date</Label>
                <Input
                  type="date"
                  value={formData.match_date}
                  onChange={(e) => updateField('match_date', e.target.value)}
                  className="h-9"
                  data-testid="match-date-input"
                />
              </div>
            </div>

            {/* Row: Toggles */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                <Label htmlFor="practice" className="text-xs cursor-pointer">Practice</Label>
                <Switch id="practice" checked={isPractice} onCheckedChange={setIsPractice} data-testid="practice-toggle" />
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                <Label htmlFor="bestOf3" className="text-xs cursor-pointer">Best of 3</Label>
                <Switch id="bestOf3" checked={bestOfThree} onCheckedChange={setBestOfThree} data-testid="best-of-3-toggle" />
              </div>
            </div>

            {/* Row: Age + Tournament */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Age Category</Label>
                <Select value={formData.age_category} onValueChange={(val) => updateField('age_category', val)}>
                  <SelectTrigger data-testid="age-category-select" className="h-9">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={`text-xs ${isPractice ? 'text-gray-400' : ''}`}>Tournament</Label>
                <Input
                  placeholder={isPractice ? 'N/A' : 'Tournament name'}
                  value={formData.tournament_name}
                  onChange={(e) => updateField('tournament_name', e.target.value)}
                  disabled={isPractice}
                  className={`h-9 ${isPractice ? 'bg-gray-100' : ''}`}
                  data-testid="tournament-input"
                />
              </div>
            </div>

            {/* Opponent */}
            <div>
              <Label className="text-xs">Opponent Name</Label>
              <Input
                placeholder="Opponent's name"
                value={formData.opponent_name}
                onChange={(e) => updateField('opponent_name', e.target.value)}
                required
                className="h-9"
                data-testid="opponent-input"
              />
            </div>

            {/* Doubles Fields */}
            {matchType === 'doubles' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Your Teammate</Label>
                  <Input
                    placeholder="Teammate"
                    value={formData.teammate_name}
                    onChange={(e) => updateField('teammate_name', e.target.value)}
                    required
                    className="h-9"
                    data-testid="teammate-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">2nd Opponent</Label>
                  <Input
                    placeholder="Opponent 2"
                    value={formData.opponent2_name}
                    onChange={(e) => updateField('opponent2_name', e.target.value)}
                    required
                    className="h-9"
                    data-testid="opponent2-input"
                  />
                </div>
              </div>
            )}

            {/* Scores */}
            {bestOfThree ? (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Game 1</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min="0" placeholder="You" value={formData.game1_user_score} onChange={(e) => updateField('game1_user_score', e.target.value)} required className="h-9" data-testid="game1-user-score" />
                    <Input type="number" min="0" placeholder="Opp" value={formData.game1_opponent_score} onChange={(e) => updateField('game1_opponent_score', e.target.value)} required className="h-9" data-testid="game1-opponent-score" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Game 2</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min="0" placeholder="You" value={formData.game2_user_score} onChange={(e) => updateField('game2_user_score', e.target.value)} required className="h-9" data-testid="game2-user-score" />
                    <Input type="number" min="0" placeholder="Opp" value={formData.game2_opponent_score} onChange={(e) => updateField('game2_opponent_score', e.target.value)} required className="h-9" data-testid="game2-opponent-score" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Game 3 (optional)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min="0" placeholder="You" value={formData.game3_user_score} onChange={(e) => updateField('game3_user_score', e.target.value)} className="h-9" data-testid="game3-user-score" />
                    <Input type="number" min="0" placeholder="Opp" value={formData.game3_opponent_score} onChange={(e) => updateField('game3_opponent_score', e.target.value)} className="h-9" data-testid="game3-opponent-score" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Your Score</Label>
                  <Input type="number" min="0" placeholder="Your score" value={formData.user_score} onChange={(e) => updateField('user_score', e.target.value)} required className="h-9" data-testid="user-score-input" />
                </div>
                <div>
                  <Label className="text-xs">Opponent Score</Label>
                  <Input type="number" min="0" placeholder="Opp score" value={formData.opponent_score} onChange={(e) => updateField('opponent_score', e.target.value)} required className="h-9" data-testid="opponent-score-input" />
                </div>
              </div>
            )}

            {/* Video URL */}
            <div>
              <Label className="text-xs">Video URL (optional)</Label>
              <Input
                placeholder="YouTube link"
                value={formData.video_url}
                onChange={(e) => updateField('video_url', e.target.value)}
                className="h-9"
                data-testid="video-url-input"
              />
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                placeholder="Match notes..."
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={2}
                data-testid="notes-input"
              />
            </div>
          </div>
          <DialogFooter className="pt-3">
            <Button type="submit" disabled={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 h-9" data-testid="submit-match-btn">
              {isLoading ? 'Logging...' : 'Log Match'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMatchDialog;
